import { v4 as uuidv4 } from "uuid";
import { z } from "zod";

import { packNetAxios } from "@/lib/api-client";
import { baseRoutes } from "@/api/base-routes";
import {
	type DownloadImportClientResponse,
	downloadImportClientResponseSchema,
	type RestoreArchivedSolutionsResponse,
	restoreArchivedSolutionsResponseSchema,
	type TenantArchivedSolutionStats,
	tenantArchivedSolutionStatsSchema,
} from "@/schemas/import/import-responses";
import {
	type ImportPipelineConfiguration,
	importPipelineConfigurationSchema,
} from "@/schemas/import/import-pipeline-configuration";
import { type SavedPackagingSolution, savedPackagingSolutionSchema } from "@/schemas/saved-packaging-solution";

export const scanImport = async (machineGroup: string, barcode: string) => {
	await packNetAxios.post<void>(`${baseRoutes.import}/v1/scan/${machineGroup}`, barcode);
};

export const postImportData = async (
	data: Record<string, number | string>[] | Record<string, number | string>,
): Promise<void> => {
	const response = await packNetAxios.post<void>(baseRoutes.import + "/v1/importData", data);
	return response.data;
};

const getImportClientResponse = async (): Promise<DownloadImportClientResponse> => {
	const response = await packNetAxios.get(`${baseRoutes.import}/v1/importClient`);

	downloadImportClientResponseSchema
		.enrich({ callSite: "getImportClientResponse" })
		.safeParseWithTelemetry(response.data);

	return response.data as DownloadImportClientResponse;
};

export const downloadImportClient = async (): Promise<void> => {
	const data = await getImportClientResponse();

	const downloadUri = data.downloadUri;

	const link = document.createElement("a");
	link.setAttribute("href", downloadUri);
	link.setAttribute("download", "");

	try {
		document.body.appendChild(link);
		link.click();
	} finally {
		document.body.removeChild(link);
	}
};

export async function getDefaultImportPipeline() {
	const response = await packNetAxios.get(`${baseRoutes.import}/v1/ImportExport/default`);

	importPipelineConfigurationSchema
		.enrich({ callSite: "getDefaultImportPipeline" })
		.safeParseWithTelemetry(response.data);

	return response.data as ImportPipelineConfiguration;
}

export async function getImportPipelineExportData(scope?: string | undefined | null) {
	const response = await packNetAxios.get(baseRoutes.import + "/v1/ImportExport", {
		params: { scope },
	});

	importPipelineConfigurationSchema
		.enrich({ callSite: "getImportPipelineExportData" })
		.safeParseWithTelemetry(response.data);

	return response.data as ImportPipelineConfiguration;
}

export async function postImportPipeline(data: ImportPipelineConfiguration, dryRun?: boolean) {
	const response = await packNetAxios.post(baseRoutes.import + "/v1/ImportExport", data, {
		params: { dryRun },
	});

	importPipelineConfigurationSchema
		.enrich({ callSite: "postImportPipelineExportData" })
		.safeParseWithTelemetry(response.data);

	return response.data as ImportPipelineConfiguration;
}

const decorateSolutionIds = (solutions: SavedPackagingSolution[]) => {
	return solutions.map((s) => ({
		...s,
		cartons: s.cartons?.map((c) => {
			delete c.media.attributes.quantity;
			return {
				...c,
				id: uuidv4(),
				prints: c.prints?.map((p) => ({ ...p, id: uuidv4() })),
				labels: c.labels?.map((l) => {
					delete l.attributes.quantity;
					return {
						...l,
						id: uuidv4(),
					};
				}),
			};
		}),
		labels: s.labels?.map((l) => {
			delete l.attributes.quantity;
			return {
				...l,
				id: uuidv4(),
			};
		}),
	}));
};

export const getPackagingSolutions = async (): Promise<SavedPackagingSolution[]> => {
	const result = await packNetAxios.get(baseRoutes.import + "/v1/entities/packagingsolutions");

	z.array(savedPackagingSolutionSchema)
		.enrich({ callSite: "getPackagingSolutions" })
		.safeParseWithTelemetry(result.data);

	const data = decorateSolutionIds(result.data);
	data.sort((a, b) => a.alias.localeCompare(b.alias));

	return data;
};

export const getPackagingSolutionById = async (solutionId: string): Promise<SavedPackagingSolution> => {
	const validation = z.uuid().enrich({ callSite: "getPackagingSolutionById" }).safeParseWithTelemetry(solutionId);

	if (!validation.success) {
		throw new Error(`Invalid packaging solution ID format: ${solutionId}`);
	}

	const result = await packNetAxios.get(baseRoutes.import + `/v1/entities/packagingsolutions/${validation.data}`);

	savedPackagingSolutionSchema.enrich({ callSite: "getPackagingSolutionById" }).safeParseWithTelemetry(result.data);

	return decorateSolutionIds([result.data])[0];
};

export const deletePackagingSolution = async (solutionId: string): Promise<void> => {
	await packNetAxios.delete(baseRoutes.import + `/v1/entities/packagingsolutions/${solutionId}`);
};

export const bulkDeletePackagingSolutions = (solutions: SavedPackagingSolution[]) =>
	updateBatchPackagingSolutions(
		solutions.map((s) => ({
			...s,
			isArchived: true,
		})),
	);

export const updateBatchPackagingSolutions = (solutions: SavedPackagingSolution[]) =>
	packNetAxios.post<void>(`${baseRoutes.import}/v1/entities/packagingSolutions/batch`, solutions);

export const putPackagingSolution = async (
	solution: SavedPackagingSolution,
	dryRun: boolean,
): Promise<SavedPackagingSolution> => {
	const response = await packNetAxios.put(
		baseRoutes.import + `/v1/entities/packagingsolutions/${solution.id}`,
		solution,
		{
			params: { dryRun },
		},
	);

	savedPackagingSolutionSchema.enrich({ callSite: "putPackagingSolution" }).safeParseWithTelemetry(response.data);

	return decorateSolutionIds([response.data])[0];
};

export const putSavedJob = (data: SavedPackagingSolution) => {
	return putPackagingSolution(data, false);
};

export const getArchivedPackagingSolutionSummary = async (): Promise<TenantArchivedSolutionStats> => {
	const response = await packNetAxios.get(`${baseRoutes.import}/v1/entities/packagingSolutions/archived-stats`);

	tenantArchivedSolutionStatsSchema
		.enrich({ callSite: "getArchivedPackagingSolutionSummary" })
		.safeParseWithTelemetry(response.data);

	return response.data as TenantArchivedSolutionStats;
};

export const restoreArchivedSavedJobs = async ({
	skipConflictingItems,
}: {
	skipConflictingItems: boolean;
}): Promise<RestoreArchivedSolutionsResponse> => {
	const response = await packNetAxios.put(
		`${baseRoutes.import}/v1/entities/packagingSolutions/restore-archived`,
		undefined,
		{
			params: {
				skipConflictingItems,
			},
		},
	);

	restoreArchivedSolutionsResponseSchema
		.enrich({ callSite: "restoreArchivedSavedJobs" })
		.safeParseWithTelemetry(response.data);

	return response.data as RestoreArchivedSolutionsResponse;
};

export const postSavedJob = async (data: SavedPackagingSolution): Promise<SavedPackagingSolution> => {
	const response = await packNetAxios.post(baseRoutes.import + "/v1/entities/packagingSolutions", data);

	savedPackagingSolutionSchema.enrich({ callSite: "postSavedJob" }).safeParseWithTelemetry(response.data);

	return response.data as SavedPackagingSolution;
};

export const submitPackagingSolutions = async (solutions: unknown[]): Promise<void> => {
	await packNetAxios.post(`${baseRoutes.import}/v1/packagingsolutions`, solutions);
};

export const toggleFavoritePackagingSolution = async (
	packagingSolutionId: string,
	isFavorite: boolean,
): Promise<void> => {
	await packNetAxios.post(`${baseRoutes.import}/v1/entities/packagingSolutions/favorite`, {
		packagingSolutionId,
		isFavorite,
	});
};

export async function getVersion(): Promise<string> {
	const response = await packNetAxios.get<string>(`${baseRoutes.import}/v1/version`);

	return response.data;
}
